import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import { Box } from '@mui/material';
import { useNotification } from '@revenuewell/front-end-bundle';
import { useConfig } from '@revenuewell/logic-config';
import { useOidcWithAdmin } from '@revenuewell/logic-oidc';
import AppointmentService from 'apps/wrapper/src/services/appointment/appointment-service';
import { AppointmentRequestDetails } from 'apps/wrapper/src/services/appointment/types/appointment-request-details';
import { AppointmentRequestNote } from 'apps/wrapper/src/services/appointment/types/appointment-request-note';
import moment from 'moment';
import { FC, useEffect, useMemo, useState } from 'react';
import { LabeledSection } from '../labeled-section/labeled-section';
import { AddNoteDialog } from './add-note-dialog/add-note-dialog';
import { NotesSectionSkeleton } from './notes-section.skeleton';
import { AddNoteIconWrapper, NoteSignature, NoteText, NoteWrapper } from './notes-section.styles';

export interface NotesSectionProps {
  locationId: number;
  requestDetails: AppointmentRequestDetails;
}

export const NotesSection: FC<NotesSectionProps> = (props) => {
  const { locationId, requestDetails } = props;
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { showError } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [notes, setNotes] = useState<AppointmentRequestNote[]>([]);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const appointmentService = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);

  useEffect(() => {
    if (!requestDetails) return;

    const init = async () => {
      setIsLoading(true);
      await loadNotes(requestDetails.id, locationId);
      setIsLoading(false);
    };

    init();
  }, [requestDetails, locationId]);

  const loadNotes = async (id: number, locId: number) => {
    try {
      const notes = await appointmentService.getAppointmentRequestNotes(id, locId);
      setNotes(notes);
    } catch (error: any) {
      if (error?.status != 404) {
        console.error('Failed to load notes: ', error);
        showError('Failed to load notes');
      }
      setNotes([]);
    }
  };

  const Note = (note: AppointmentRequestNote) => {
    return (
      <NoteWrapper>
        <NoteText>{note.note}</NoteText>
        <NoteSignature>
          <Box sx={{ fontWeight: 500 }}>{note.name || 'Unknown'}</Box>&nbsp;-{' '}
          {moment(note.systemTimestamp).format('MM/DD/YYYY [at] h:mm a')}
        </NoteSignature>
      </NoteWrapper>
    );
  };

  const handleAddNoteClick = () => {
    setNoteDialogOpen(true);
  };

  const handleNoteAdded = async () => {
    if (!requestDetails) return;
    await loadNotes(requestDetails.id, locationId);
    setNoteDialogOpen(false);
  };

  if (isLoading) return <NotesSectionSkeleton />;

  return (
    <>
      <LabeledSection
        label="Notes"
        labelPlaceholder={
          <AddNoteIconWrapper>
            <AddBoxOutlinedIcon onClick={handleAddNoteClick} />
          </AddNoteIconWrapper>
        }
      >
        {!isLoading && notes.length === 0 && <Box sx={{ padding: '10px' }}>No notes</Box>}
        {!isLoading && notes.map((note, index) => <Note key={index} {...note} />)}
      </LabeledSection>

      {requestDetails && (
        <AddNoteDialog
          opened={noteDialogOpen}
          appointmentRequestId={requestDetails.id}
          locationId={locationId}
          notes={notes}
          onAddNode={handleNoteAdded}
          onClose={() => setNoteDialogOpen(false)}
        />
      )}
    </>
  );
};
